import React, { useEffect, useState } from 'react';
import { Settings as SettingsIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGlobalTheme } from '../../hooks/useGlobalTheme';
import SettingsBody from './SettingsBody';

/**
 * Lightweight shell — paints immediately on /settings navigation.
 * Heavy SettingsBody (10k+ lines, 120+ hooks) mounts on the next frame so clicks feel instant.
 */
function SettingsShell() {
  const { t } = useTranslation();
  const { isDashboardLightMode } = useGlobalTheme();
  const inkPrimary = isDashboardLightMode ? '#0f172a' : '#ffffff';
  const inkMuted = isDashboardLightMode ? 'rgba(15, 23, 42, 0.62)' : 'rgba(255, 255, 255, 0.6)';

  return (
    <div className="settings-container mx-auto max-w-full min-w-0 space-y-6 animate-pulse">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1
            style={{
              fontSize: '1.875rem',
              fontWeight: '700',
              color: inkPrimary,
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
            }}
          >
            <SettingsIcon size={32} className={isDashboardLightMode ? 'text-blue-600' : 'text-blue-400'} />
            {t('settings.title')}
          </h1>
          <p style={{ fontSize: '0.875rem', color: inkMuted, marginTop: '0.5rem', margin: 0 }}>
            {t('settings.subtitle')}
          </p>
        </div>
      </div>
      <div
        style={{
          borderRadius: '1rem',
          padding: '1.5rem',
          background: isDashboardLightMode ? '#f8fafc' : 'rgba(255, 255, 255, 0.06)',
          minHeight: '420px',
        }}
      />
    </div>
  );
}

export default function Settings() {
  const [bodyReady, setBodyReady] = useState(false);

  useEffect(() => {
    let innerFrame;
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => setBodyReady(true));
    });
    return () => {
      cancelAnimationFrame(outerFrame);
      if (innerFrame) cancelAnimationFrame(innerFrame);
    };
  }, []);

  if (!bodyReady) {
    return <SettingsShell />;
  }

  return <SettingsBody />;
}
